import type { ReactNode } from 'react';
import { cn } from './cn';

export type TagTone = 'neutral' | 'green' | 'gold' | 'blue' | 'red' | 'purple' | 'cyan';

const TONES: Record<TagTone, string> = {
  neutral: 'bg-surface-3 text-muted',
  green: 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300',
  gold: 'bg-harvest-50 text-harvest-700 dark:bg-harvest-400/15 dark:text-harvest-300',
  blue: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
  red: 'bg-danger-500/10 text-danger-500',
  purple: 'bg-[#722ed1]/10 text-[#722ed1] dark:text-[#b37feb]',
  cyan: 'bg-[#13c2c2]/12 text-[#0f9b9b] dark:text-[#36cfc9]',
};

// antd Tag `color` → our tone. Accepts named antd colors + a few aliases; unknown
// (incl. arbitrary hex) falls back to neutral.
const COLOR_TO_TONE: Record<string, TagTone> = {
  green: 'green', success: 'green', lime: 'green',
  red: 'red', error: 'red', volcano: 'red',
  blue: 'blue', geekblue: 'blue', processing: 'blue',
  cyan: 'cyan',
  gold: 'gold', orange: 'gold', warning: 'gold', yellow: 'gold',
  purple: 'purple', magenta: 'purple',
  default: 'neutral',
};

export interface TagProps {
  /** Preferred: a semantic tone. Also accepts any string (unknown → neutral). */
  tone?: TagTone | (string & {});
  /** antd compatibility — a `color` name is mapped to a tone. */
  color?: string;
  children: ReactNode;
  className?: string;
  style?: import('react').CSSProperties;
  onClick?: import('react').MouseEventHandler<HTMLSpanElement>;
  title?: string;
  /** Optional leading icon (antd Tag `icon`). */
  icon?: ReactNode;
  bordered?: boolean;
  closable?: boolean;
  onClose?: () => void;
  /** Spoken name, for a pill whose text is an abbreviation ("SP" reads as "Sales Person"). */
  'aria-label'?: string;
}

/** Small status pill (replaces antd Tag). */
export function Tag({ tone, color, children, className, style, onClick, title, icon, 'aria-label': ariaLabel }: TagProps) {
  const resolved: TagTone = color ? (COLOR_TO_TONE[color] ?? 'neutral') : (tone && tone in TONES ? (tone as TagTone) : 'neutral');
  return (
    <span
      onClick={onClick}
      style={style}
      title={title}
      aria-label={ariaLabel}
      className={cn(
        // A pill, matching RoleBadge and every other chip in the app — Tag was the one
        // component still drawing a status as a tight rectangle. Slightly more horizontal
        // padding so short labels ('NEW', 'PAID') stop looking cramped inside their own border.
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 type-caption-strong leading-tight',
        TONES[resolved],
        onClick && 'cursor-pointer',
        className,
      )}
    >
      {icon != null && <span className="inline-flex items-center [&>svg]:size-3">{icon}</span>}
      {children}
    </span>
  );
}
